export type ErrorOrigin = 'server' | 'client';

/**
 * Critical application error that should be caught by error boundaries
 * and reported to monitoring services like Grafana Faro
 */
export class CriticalApplicationError extends Error {
  public readonly origin: ErrorOrigin;
  public readonly timestamp: string;
  public readonly innerError?: Error;

  constructor(message: string, origin: ErrorOrigin, innerError?: Error) {
    super(message);
    this.name = 'CriticalApplicationError';
    this.origin = origin;
    this.timestamp = new Date().toISOString();
    this.innerError = innerError;

    // Maintain proper stack trace for V8 engines
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, CriticalApplicationError);
    }

    // If we have an inner error, preserve its stack
    if (innerError && innerError.stack) {
      this.stack = `${this.stack}\n\nInner Error: ${innerError.stack}`;
    }
  }
}

/**
 * Extension content scripts that inject into an *isolated* world never
 * surface to the host page's error reporting at all — invisible by browser
 * design, nothing to detect. This only catches the two ways an extension
 * DOES become visible to us: a main-world injected `<script>` referencing
 * its own extension:// URL in the stack, or (far more common) an extension
 * mutating the DOM out from under React, which throws when React's
 * reconciler tries to remove/move a node an extension already relocated —
 * a well-documented signature (Grammarly, password managers, ad blockers,
 * translation tools). Heuristic, not certain — a false positive here just
 * means a genuine DOM bug gets mislabeled, not lost; it's still reported
 * either way.
 */
const EXTENSION_PROTOCOL_PATTERN =
  /chrome-extension:\/\/|moz-extension:\/\/|safari-extension:\/\/|safari-web-extension:\/\//i;
const DOM_MUTATION_SIGNATURE_PATTERN =
  /removeChild|insertBefore|is not a child of this node|The object can not be found here/i;

export function isLikelyBrowserExtensionCrash(error: Error): boolean {
  return (
    DOM_MUTATION_SIGNATURE_PATTERN.test(error.message) ||
    EXTENSION_PROTOCOL_PATTERN.test(error.stack ?? '')
  );
}

/**
 * webpack throws `ChunkLoadError` ("Loading chunk 866 failed.") when a
 * lazily-loaded JS chunk can't be fetched — almost always a transient network
 * drop (school content filter, flaky mobile/Wi-Fi), since the chunks for the
 * running build are still on the CDN. Safari drops the `name`, so match the
 * message too. CSS chunks use "Loading CSS chunk …".
 */
const CHUNK_LOAD_MESSAGE_PATTERN = /Loading (CSS )?chunk [\w-]+ failed/i;

export function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    CHUNK_LOAD_MESSAGE_PATTERN.test(error.message)
  );
}

const CHUNK_RELOAD_KEY = 'chunkLoadAutoReloadAt';
const CHUNK_RELOAD_COOLDOWN_MS = 30_000;

/**
 * True at most once per cooldown window per tab, so a chunk that is really
 * gone (or a network that stays down) shows the fallback screen instead of
 * reload-looping. Records the attempt as a side effect. sessionStorage can
 * throw (privacy mode, blocked storage) — no guard means no auto-reload.
 */
export function claimChunkLoadAutoReload(now = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    if (now - last < CHUNK_RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}
